import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from "../components/HeaderA";
import { translateServerMsg } from '../utils/serverMessages';

const translations = {
  el: {
    title: "Επικοινωνήστε μαζί μας",
    subtitle: "Θα χαρούμε να σας ακούσουμε! Συμπληρώστε την παρακάτω φόρμα και θα επικοινωνήσουμε μαζί σας σύντομα.",
    labelName: "Ονοματεπώνυμο",
    labelEmail: "Email",
    labelMessage: "Μήνυμα",
    placeholderName: "Εισάγετε το όνομά σας",
    placeholderEmail: "Εισάγετε το email σας",
    placeholderMessage: "Εισάγετε το μήνυμά σας",
    sendBtn: "Αποστολή μηνύματος",
    validationError: "Παρακαλώ συμπληρώστε όλα τα πεδία.",
    successMsg: "Το μήνυμα στάλθηκε με επιτυχία! Ευχαριστούμε που επικοινωνήσατε μαζί μας.",
    errorMsg: "Κάτι πήγε στραβά. Παρακαλώ δοκιμάστε ξανά.",
    fetchError: "Αποτυχία αποστολής μηνύματος. Παρακαλώ δοκιμάστε αργότερα.",
    successTitle: "Επιτυχία!",
    errorTitle: "Σφάλμα",
    validationTitle: "Συμπληρώστε όλα τα πεδία",
    ok: "Εντάξει"
  },
  en: {
    title: "Contact us",
    subtitle: "We’d love to hear from you! Please fill out the form below and we’ll get back to you soon.",
    labelName: "Full name",
    labelEmail: "Email",
    labelMessage: "Message",
    placeholderName: "Enter your name",
    placeholderEmail: "Enter email",
    placeholderMessage: "Enter your message",
    sendBtn: "Send message",
    validationError: "Please fill in all fields.",
    successMsg: "Message sent successfully! Thank you for reaching out.",
    errorMsg: "Something went wrong. Please try again.",
    fetchError: "Failed to send message. Please try again later.",
    successTitle: "Success!",
    errorTitle: "Error",
    validationTitle: "Fill in all fields",
    ok: "OK"
  }
};

const Contact = () => {
  const navigate = useNavigate();
  const [fullname, setFullname] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [popup, setPopup] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  //Initialize language state from localStorage, defaults to 'el' if not set
  const [lang, setLang] = useState(localStorage.getItem("app_lang") || "el");

  //Synchronize language with HeaderA when language is changed elsewhere
  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem("app_lang") || "el");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const t = translations[lang];

  const handleContact = async (e) => {
    e.preventDefault();

    if (!fullname || !email || !message) {
      setPopup({ title: t.validationTitle, msg: t.validationError });
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fullname, email, message }),
      });

      const data = await res.json();

      if (!res.ok) {
        setPopup({ title: t.errorTitle, msg: translateServerMsg(data.msg, lang) || t.errorMsg });
        return;
      }

      setShowSuccessPopup(true);
      setFullname('');
      setEmail('');
      setMessage('');
    } catch (error) {
      console.error("Error sending message:", error);
      setPopup({ title: t.errorTitle, msg: t.fetchError });
      setFullname('');
      setEmail('');
      setMessage('');
    }
  };

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />
      <main id="main-content" className="flex-1 flex items-center justify-center px-4 py-8">
        <form 
          onSubmit={handleContact}
          aria-labelledby="contact-form-title"
          noValidate
          className="w-full max-w-md bg-gradient-to-b from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 p-6 rounded-3xl shadow-lg flex flex-col gap-4">
          
          <h1 id="contact-form-title" className="text-2xl font-bold text-center text-custom-letters mb-2">{t.title}</h1>
          <p className="text-center text-custom-letters mb-4 text-sm italic">
            {t.subtitle}
          </p>

          <label htmlFor="contact-name" className="text-custom-letters font-semibold">{t.labelName}</label>
          <input 
            id="contact-name"
            type="text" 
            placeholder={t.placeholderName} 
            value={fullname} 
            onChange={e => setFullname(e.target.value)} 
            required
            autoComplete="name"
            className="p-2 rounded-lg text-black outline-none focus:ring-2 focus:ring-custom-header"
          />

          <label htmlFor="contact-email" className="text-custom-letters font-semibold">{t.labelEmail}</label>
          <input 
            id="contact-email"
            type="email" 
            placeholder={t.placeholderEmail} 
            value={email} 
            onChange={e => setEmail(e.target.value)} 
            required
            autoComplete="email"
            className="p-2 rounded-lg text-black outline-none focus:ring-2 focus:ring-custom-header"
          />

          <label htmlFor="contact-message" className="text-custom-letters font-semibold">{t.labelMessage}</label>
          <textarea
            id="contact-message"
            placeholder={t.placeholderMessage}
            value={message}
            onChange={e => setMessage(e.target.value)}
            required
            className="p-2 rounded-lg text-black min-h-[120px] outline-none focus:ring-2 focus:ring-custom-header"
          />

          <button 
            type="submit" 
            className="bg-custom-header text-custom-letters font-bold py-2 rounded-lg mt-2 hover:bg-gray-200 transition">
            {t.sendBtn}
          </button>
        </form>
      </main>

      {popup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
          <div role="dialog" aria-modal="true" aria-labelledby="contact-popup-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
            <h3 id="contact-popup-title" className="text-lg font-semibold text-custom-letters mb-3">{popup.title}</h3>
            <p className="text-sm text-custom-letters mb-6">{popup.msg}</p>
            <button
              onClick={() => setPopup(null)}
              className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-custom-letters font-bold">
              {t.ok}
            </button>
          </div>
        </div>
      )}

      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
          <div role="dialog" aria-modal="true" aria-labelledby="contact-success-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
            <h3 id="contact-success-title" className="text-lg font-semibold text-custom-letters mb-3">{t.successTitle}</h3>
            <p className="text-sm text-custom-letters mb-6">{t.successMsg}</p>
            <button
              onClick={() => { setShowSuccessPopup(false); navigate('/contact'); }}
              className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-custom-letters font-bold">
              {t.ok}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;